import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import {
  DuracionTarea,
  TipoAgarreNiosh,
  DatosPuntoNiosh,
  EvaluacionTareaNioshInput,
  ResultadoTareaNiosh,
  calcularEvaluacionTareaNiosh,
  TareaNioshMultitarea,
  ResultadoMultitareaNiosh,
  calcularIndiceLevantamientoCompuesto,
} from '../lib/niosh';
import {
  createNioshAssessment,
  fetchNioshAssessments,
  NioshAssessmentRow,
  NioshTareaGuardada,
} from '../lib/gestion-tecnica-niosh';
import { generateNioshReportPDF } from '../lib/gestion-tecnica-niosh-pdf';
import { fetchJobPositionsFull, JobPositionLegalFields } from '../lib/gestion-tecnica-constructor';

// Mismos estilos base que RebaMethodologyPage.tsx / RulaMethodologyPage.tsx
// (fondo oscuro, números principales en 34px).
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', color: '#e2e8f0', fontSize: 13,
};

const fieldDescStyle: React.CSSProperties = {
  fontSize: 12.5, color: '#94a3b8', margin: '0 0 8px 0', lineHeight: 1.45,
};

// Colores por nivel de riesgo NIOSH (LI/ILC): aceptable=verde,
// moderado=ámbar, alto=rojo. Mismo esquema visual que NIVEL_COLORES de
// RULA/REBA, pero indexado por el string de nivel en vez de un número
// (NIOSH no tiene niveles numerados en la fuente).
const NIVEL_COLORES_NIOSH: Record<'aceptable' | 'moderado' | 'alto', { bg: string; border: string; text: string }> = {
  aceptable: { bg: 'rgba(47,191,113,0.12)', border: 'rgba(47,191,113,0.3)', text: '#2FBF71' },
  moderado: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b' },
  alto: { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
};

function ResultadoIntermedio({ label, valor }: { label: string; valor: number | string }) {
  return (
    <div>
      <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
      <div style={{ fontSize: 20, fontWeight: 600, color: '#e2e8f0' }}>{valor}</div>
    </div>
  );
}

// ===== Estado de formulario de una tarea (vive solo en este archivo) =====
// Todos los campos numéricos como string (para inputs controlados vacíos
// sin forzar 0), convertidos a number con parseFloat(x) || 0 al calcular.
interface TareaFormNiosh {
  id: string;
  nombre: string;
  origenH: string;
  origenV: string;
  origenA: string;
  origenAgarre: TipoAgarreNiosh;
  tieneDestino: boolean;
  destinoH: string;
  destinoV: string;
  destinoA: string;
  destinoAgarre: TipoAgarreNiosh;
  frecuencia: string;
  duracion: DuracionTarea;
  peso: string;
  unaMano: boolean;
  multiplesTrabajadores: boolean;
}

function crearTareaVacia(): TareaFormNiosh {
  return {
    id: `tarea-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    nombre: '',
    origenH: '0',
    origenV: '0',
    origenA: '0',
    origenAgarre: 'bueno',
    tieneDestino: false,
    destinoH: '0',
    destinoV: '0',
    destinoA: '0',
    destinoAgarre: 'bueno',
    frecuencia: '1',
    duracion: 'corta',
    peso: '0',
    unaMano: false,
    multiplesTrabajadores: false,
  };
}

// Sin control significativo en destino, D se calcula como 0 usando el
// mismo V de origen dos veces — D siempre se requiere por el motor,
// pero sin destino real no hay desplazamiento vertical relevante.
function tareaFormATareaInput(t: TareaFormNiosh): EvaluacionTareaNioshInput {
  const origenV = parseFloat(t.origenV) || 0;
  const origen: DatosPuntoNiosh = {
    H: parseFloat(t.origenH) || 0,
    V: origenV,
    A: parseFloat(t.origenA) || 0,
    agarre: t.origenAgarre,
  };
  const destinoV = parseFloat(t.destinoV) || 0;
  const destino: DatosPuntoNiosh | undefined = t.tieneDestino
    ? { H: parseFloat(t.destinoH) || 0, V: destinoV, A: parseFloat(t.destinoA) || 0, agarre: t.destinoAgarre }
    : undefined;

  return {
    origen,
    destino,
    vOrigenParaD: origenV,
    vDestinoParaD: t.tieneDestino ? destinoV : origenV,
    frecuencia: parseFloat(t.frecuencia) || 0,
    duracion: t.duracion,
    peso: parseFloat(t.peso) || 0,
    unaMano: t.unaMano,
    multiplesTrabajadores: t.multiplesTrabajadores,
  };
}

export default function NioshMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── Plan gate (after all hooks, mismo criterio que REBA/RULA) ──
  const planAllowed = isSuperAdmin || !displayCompany || hasPlanAccess(displayCompany.plan, 'metodologia-niosh');

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [savedAssessments, setSavedAssessments] = useState<NioshAssessmentRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [descripcion, setDescripcion] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');

  const [tareas, setTareas] = useState<TareaFormNiosh[]>([crearTareaVacia()]);

  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [ultimaEvaluacionGuardada, setUltimaEvaluacionGuardada] = useState<NioshAssessmentRow | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyId) { setLoadingData(false); return; }
    setLoadingData(true);
    try {
      const [positions, assessments] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchNioshAssessments(companyId),
      ]);
      setJobPositions(positions);
      setSavedAssessments(assessments);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const agregarTarea = () => setTareas(prev => [...prev, crearTareaVacia()]);
  const eliminarTarea = (id: string) => setTareas(prev => prev.length > 1 ? prev.filter(t => t.id !== id) : prev);
  const actualizarTarea = (id: string, cambios: Partial<TareaFormNiosh>) =>
    setTareas(prev => prev.map(t => t.id === id ? { ...t, ...cambios } : t));

  // ===== Cálculo en vivo (sin botón "Calcular") =====
  const resultadoPorTarea: { tarea: TareaFormNiosh; resultado: ResultadoTareaNiosh }[] = tareas.map(t => ({
    tarea: t,
    resultado: calcularEvaluacionTareaNiosh(tareaFormATareaInput(t)),
  }));

  const tareasParaCompuesto: TareaNioshMultitarea[] = tareas.map(t => ({ id: t.id, input: tareaFormATareaInput(t) }));
  const resultadoCompuesto: ResultadoMultitareaNiosh = calcularIndiceLevantamientoCompuesto(tareasParaCompuesto);
  const coloresCompuesto = NIVEL_COLORES_NIOSH[resultadoCompuesto.nivelRiesgo];

  const puedeGuardar = descripcion.trim().length > 0;

  const handleGuardar = async () => {
    setError(null);
    if (!descripcion.trim()) { setError('Ingresa una descripción del local/puesto/tarea evaluada'); return; }

    setGuardando(true);
    setGuardadoOk(false);
    try {
      const tareasGuardadas: NioshTareaGuardada[] = tareas.map(t => ({
        id: t.id,
        input: tareaFormATareaInput(t),
        resultado: calcularEvaluacionTareaNiosh(tareaFormATareaInput(t)),
      }));

      await createNioshAssessment({
        companyId,
        jobPositionId: selectedJobPositionId || undefined,
        descripcion,
        tareas: tareasGuardadas,
        resultadoCompuesto,
        createdBy: profile?.id ?? null,
      });
      // createNioshAssessment() no retorna el registro insertado (mismo
      // patrón que createRebaAssessment()) — se vuelve a consultar para
      // obtener el registro recién creado con su id real, necesario para
      // el PDF.
      const assessments = await fetchNioshAssessments(companyId);
      setSavedAssessments(assessments);
      setUltimaEvaluacionGuardada(assessments[0] ?? null);
      setGuardadoOk(true);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar la evaluación');
    } finally {
      setGuardando(false);
    }
  };

  const handleDescargarPdf = async () => {
    if (!ultimaEvaluacionGuardada) {
      setError('No hay una evaluación guardada para generar el PDF');
      return;
    }
    setDescargandoPdf(true);
    try {
      await generateNioshReportPDF(
        ultimaEvaluacionGuardada,
        companyId,
        displayCompany?.name ?? 'Empresa',
        profile?.id ?? null
      );
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el PDF');
    } finally {
      setDescargandoPdf(false);
    }
  };

  if (!planAllowed) {
    return <UpgradeWall {...PLAN_FEATURES['metodologia-niosh']} />;
  }

  if (loadingData) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Manejo Manual de Cargas — Ecuación de NIOSH</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Revised NIOSH Lifting Equation. Fuente: NIOSH [1994], DHHS Publication No. 94-110.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Descripción (local, puesto o tarea evaluada)</label>
        <input
          style={inputStyle}
          type="text"
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Ej. Operador de bodega, Reponedor..."
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Puesto de trabajo (opcional)</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo (opcional)" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Sin asociar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      {/* ===== Tareas ===== */}
      {resultadoPorTarea.map(({ tarea, resultado }, index) => {
        const coloresTarea = NIVEL_COLORES_NIOSH[resultado.nivelRiesgo];
        return (
          <div key={tarea.id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Tarea {index + 1} — Nombre</label>
                <input
                  style={inputStyle}
                  type="text"
                  value={tarea.nombre}
                  onChange={e => actualizarTarea(tarea.id, { nombre: e.target.value })}
                  placeholder="Ej. Levantar cajas desde mesa"
                />
              </div>
              <button
                onClick={() => eliminarTarea(tarea.id)}
                disabled={tareas.length === 1}
                style={{
                  background: 'transparent', color: '#ef4444', border: '1px solid #7f1d1d', borderRadius: 6,
                  padding: '8px 14px', fontSize: 13, cursor: tareas.length === 1 ? 'not-allowed' : 'pointer',
                  opacity: tareas.length === 1 ? 0.4 : 1,
                }}
              >
                Eliminar
              </button>
            </div>

            <h3 style={{ fontSize: 13, color: '#e2e8f0', marginTop: 0, marginBottom: 8 }}>Origen</h3>
            <p style={fieldDescStyle}>
              Distancia horizontal (H), distancia vertical (V) de las manos al suelo, y ángulo de asimetría (A) en el punto de origen del levantamiento.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>H (cm)</label>
                <input style={inputStyle} type="number" value={tarea.origenH} onChange={e => actualizarTarea(tarea.id, { origenH: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>V (cm)</label>
                <input style={inputStyle} type="number" value={tarea.origenV} onChange={e => actualizarTarea(tarea.id, { origenV: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>A (grados)</label>
                <input style={inputStyle} type="number" value={tarea.origenA} onChange={e => actualizarTarea(tarea.id, { origenA: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Agarre</label>
                <select style={inputStyle} value={tarea.origenAgarre} onChange={e => actualizarTarea(tarea.id, { origenAgarre: e.target.value as TipoAgarreNiosh })}>
                  <option value="bueno" style={{ color: '#000' }}>Bueno</option>
                  <option value="regular" style={{ color: '#000' }}>Regular</option>
                  <option value="malo" style={{ color: '#000' }}>Malo</option>
                </select>
              </div>
            </div>

            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={tarea.tieneDestino}
                onChange={e => actualizarTarea(tarea.id, { tieneDestino: e.target.checked })} />
              Existe control significativo de carga en destino
            </label>

            {tarea.tieneDestino && (
              <>
                <h3 style={{ fontSize: 13, color: '#e2e8f0', marginTop: 12, marginBottom: 8 }}>Destino</h3>
                <p style={fieldDescStyle}>
                  Distancia horizontal (H), distancia vertical (V) y ángulo de asimetría (A) en el punto de destino del levantamiento.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={labelStyle}>H (cm)</label>
                    <input style={inputStyle} type="number" value={tarea.destinoH} onChange={e => actualizarTarea(tarea.id, { destinoH: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>V (cm)</label>
                    <input style={inputStyle} type="number" value={tarea.destinoV} onChange={e => actualizarTarea(tarea.id, { destinoV: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>A (grados)</label>
                    <input style={inputStyle} type="number" value={tarea.destinoA} onChange={e => actualizarTarea(tarea.id, { destinoA: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>Agarre</label>
                    <select style={inputStyle} value={tarea.destinoAgarre} onChange={e => actualizarTarea(tarea.id, { destinoAgarre: e.target.value as TipoAgarreNiosh })}>
                      <option value="bueno" style={{ color: '#000' }}>Bueno</option>
                      <option value="regular" style={{ color: '#000' }}>Regular</option>
                      <option value="malo" style={{ color: '#000' }}>Malo</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 4, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Frecuencia (elev/min)</label>
                <input style={inputStyle} type="number" value={tarea.frecuencia} onChange={e => actualizarTarea(tarea.id, { frecuencia: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Duración de la tarea</label>
                <select style={inputStyle} value={tarea.duracion} onChange={e => actualizarTarea(tarea.id, { duracion: e.target.value as DuracionTarea })}>
                  <option value="corta" style={{ color: '#000' }}>Corta (&lt;1 hora)</option>
                  <option value="moderada" style={{ color: '#000' }}>Moderada (1-2 horas)</option>
                  <option value="larga" style={{ color: '#000' }}>Larga (2-8 horas)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Peso de la carga (kg)</label>
                <input style={inputStyle} type="number" value={tarea.peso} onChange={e => actualizarTarea(tarea.id, { peso: e.target.value })} />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={checkboxLabelStyle}>
                <input type="checkbox" checked={tarea.unaMano}
                  onChange={e => actualizarTarea(tarea.id, { unaMano: e.target.checked })} />
                Levantamiento con una sola mano (×0.6)
              </label>
              <label style={checkboxLabelStyle}>
                <input type="checkbox" checked={tarea.multiplesTrabajadores}
                  onChange={e => actualizarTarea(tarea.id, { multiplesTrabajadores: e.target.checked })} />
                Levantamiento por 2+ trabajadores (×0.85)
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
              <ResultadoIntermedio label="D" valor={`${resultado.D.toFixed(1)} cm`} />
              <ResultadoIntermedio label="RWL origen" valor={`${resultado.rwlOrigen.toFixed(2)} kg`} />
              <ResultadoIntermedio label="RWL destino" valor={resultado.rwlDestino !== null ? `${resultado.rwlDestino.toFixed(2)} kg` : '—'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
              <ResultadoIntermedio label="RWL final" valor={`${resultado.rwlFinal.toFixed(2)} kg`} />
              <ResultadoIntermedio label="Índice de Levantamiento (LI)" valor={resultado.li.toFixed(2)} />
            </div>

            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
                background: coloresTarea.bg, border: `1px solid ${coloresTarea.border}`,
              }}
            >
              <strong style={{ color: coloresTarea.text, fontSize: 14, textTransform: 'capitalize' }}>{resultado.nivelRiesgo}</strong>
            </div>
          </div>
        );
      })}

      <button
        onClick={agregarTarea}
        style={{
          background: 'transparent', color: '#2FBF71', border: '1px solid #2FBF71', borderRadius: 6,
          padding: '8px 16px', fontWeight: 600, cursor: 'pointer', marginBottom: 20,
        }}
      >
        + Agregar otra tarea
      </button>

      {/* ===== Resultado compuesto ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Índice de Levantamiento Compuesto</h2>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0', marginBottom: 16 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>Tarea</th>
              <th style={{ padding: '6px 8px' }}>LI propio</th>
              <th style={{ padding: '6px 8px' }}>Frecuencia acumulada</th>
              <th style={{ padding: '6px 8px' }}>LI a frecuencia acumulada</th>
              <th style={{ padding: '6px 8px' }}>Incremento</th>
            </tr>
          </thead>
          <tbody>
            {resultadoCompuesto.ordenPorLI.map(t => {
              const nombre = tareas.find(f => f.id === t.id)?.nombre || t.id;
              return (
                <tr key={t.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{nombre}</td>
                  <td style={{ padding: '6px 8px' }}>{t.liPropio.toFixed(2)}</td>
                  <td style={{ padding: '6px 8px' }}>{t.frecuenciaAcumuladaHastaAqui}</td>
                  <td style={{ padding: '6px 8px' }}>{t.liAFrecuenciaAcumulada.toFixed(2)}</td>
                  <td style={{ padding: '6px 8px' }}>{t.incremento.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <span style={{ color: '#94a3b8', fontSize: 12 }}>Índice de Levantamiento Compuesto (ILC)</span>
        <div style={{ fontSize: 34, fontWeight: 600, color: '#e2e8f0', marginBottom: 14 }}>{resultadoCompuesto.ilc.toFixed(2)}</div>

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
            background: coloresCompuesto.bg, border: `1px solid ${coloresCompuesto.border}`, marginBottom: 12,
          }}
        >
          <strong style={{ color: coloresCompuesto.text, fontSize: 14, textTransform: 'capitalize' }}>{resultadoCompuesto.nivelRiesgo}</strong>
        </div>

        <p style={fieldDescStyle}>
          Métodos alternativos de referencia (no recomendados): suma de riesgos={resultadoCompuesto.sumaDeRiesgos.toFixed(2)},
          {' '}riesgo promedio={resultadoCompuesto.riesgoPromedio.toFixed(2)}, mayor riesgo={resultadoCompuesto.mayorRiesgo.toFixed(2)}.
        </p>
      </div>

      <button
        onClick={handleGuardar}
        disabled={guardando || !puedeGuardar}
        style={{
          background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
          padding: '8px 16px', fontWeight: 600, cursor: guardando || !puedeGuardar ? 'not-allowed' : 'pointer',
          opacity: guardando || !puedeGuardar ? 0.6 : 1,
        }}
      >
        {guardando ? 'Guardando...' : 'Guardar evaluación'}
      </button>

      {guardadoOk && (
        <div style={{ color: '#2FBF71', fontSize: 13, marginTop: 10 }}>✓ Evaluación guardada correctamente</div>
      )}

      {guardadoOk && ultimaEvaluacionGuardada && (
        <button
          onClick={handleDescargarPdf}
          disabled={descargandoPdf}
          style={{
            background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontWeight: 600, cursor: descargandoPdf ? 'not-allowed' : 'pointer',
            opacity: descargandoPdf ? 0.6 : 1, marginTop: 10, marginLeft: 10,
          }}
        >
          {descargandoPdf ? 'Generando PDF...' : 'Descargar reporte PDF'}
        </button>
      )}

      {savedAssessments.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Evaluaciones guardadas</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Descripción</th>
                <th style={{ padding: '6px 8px' }}>Nº de tareas</th>
                <th style={{ padding: '6px 8px' }}>ILC</th>
                <th style={{ padding: '6px 8px' }}>Nivel</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {savedAssessments.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{a.descripcion}</td>
                  <td style={{ padding: '6px 8px' }}>{a.tareas.length}</td>
                  <td style={{ padding: '6px 8px' }}>{a.resultado_compuesto.ilc.toFixed(2)}</td>
                  <td style={{ padding: '6px 8px', textTransform: 'capitalize' }}>{a.resultado_compuesto.nivelRiesgo}</td>
                  <td style={{ padding: '6px 8px' }}>{new Date(a.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
